import styled from "styled-components";

const blue = "#1499df";
const lime = "#a9e64d";
const ink = "#13242d";

export const Hero = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(64px, 8vw, 110px) max(7vw, 48px);
  overflow: hidden;
  color: #ffffff;
  background: radial-gradient(circle at 76% 36%, #15516b 0%, #0d3546 34%, #111719 72%);
`;

export const HeroInner = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, .95fr) minmax(360px, .72fr);
  gap: clamp(42px, 7vw, 96px);
  align-items: center;
  max-width: 1120px;
  margin: 0 auto;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

export const HeroCopy = styled.div`
  max-width: 570px;

  span {
    display: block;
    margin-bottom: 18px;
    color: ${lime};
    font-size: 12px;
    line-height: 18px;
    font-weight: 620;
    letter-spacing: .08em;
  }

  h2 {
    margin: 0 0 22px;
    font-size: clamp(42px, 5.8vw, 76px);
    line-height: 1.01;
    font-weight: 560;
    letter-spacing: -.045em;
  }

  p {
    max-width: 520px;
    margin: 0;
    color: #c3d2d8;
    font-size: 18px;
    line-height: 29px;
  }
`;

export const IcebergStage = styled.div`
  position: relative;
  width: min(100%, 430px);
  height: clamp(560px, 57vw, 680px);
  margin: 0 auto;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 28px 34px rgba(0,0,0,.26));
  }

  @media (max-width: 520px) {
    height: 560px;
  }
`;

export const IcebergHotspot = styled.button<{ x: string; y: string }>`
  position: absolute;
  top: ${(props) => props.y};
  left: ${(props) => props.x};
  z-index: 3;
  width: 29px;
  height: 29px;
  padding: 0;
  border: 2px solid rgba(255,255,255,.9);
  border-radius: 50%;
  color: #ffffff;
  background: rgba(11,129,190,.94);
  box-shadow: 0 0 0 7px rgba(30,165,229,.18);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  line-height: 25px;
  text-align: center;
  transform: translate(-50%, -50%);
  transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    outline: none;
    transform: translate(-50%, -50%) scale(1.15);
    box-shadow: 0 0 0 10px rgba(166,229,74,.22);
    background: #159cda;
  }
`;

export const IcebergInstruction = styled.span`
  position: absolute;
  z-index: 4;
  top: 2%;
  right: 0;
  padding: 6px 10px;
  border-radius: 999px;
  color: #d9e9ef;
  background: rgba(7,30,40,.72);
  font-size: 10px;
  line-height: 15px;
  font-style: italic;
`;

export const IcebergTooltip = styled.div`
  position: absolute;
  z-index: 4;
  right: 2%;
  bottom: 2%;
  left: 2%;
  padding: 14px 16px;
  border: 1px solid rgba(255,255,255,.22);
  border-radius: 10px;
  color: #ffffff;
  background: rgba(7,30,40,.9);
  backdrop-filter: blur(7px);

  strong,
  span { display: block; }

  strong {
    margin-bottom: 3px;
    font-size: 13px;
    line-height: 19px;
    font-weight: 580;
  }

  span {
    color: #b9ccd4;
    font-size: 11px;
    line-height: 17px;
  }
`;

export const Question = styled.p`
  margin: 0;
  padding-left: 24px;
  border-left: 3px solid ${blue};
  color: var(--project-emphasis);
  font-size: 23px;
  line-height: 34px;
  font-weight: 520;
  text-align: left;
`;

export const ExpansionSection = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 98px) max(7vw, 48px);
  background: #f4f8fa;
`;

export const ExpansionInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const ExpansionHeader = styled.div`
  max-width: 760px;
  margin-bottom: 30px;

  span {
    display: block;
    margin-bottom: 9px;
    color: #148bc6;
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
    letter-spacing: .08em;
  }

  h3 {
    margin: 0 0 12px;
    color: ${ink};
    font-size: clamp(31px, 4vw, 50px);
    line-height: 1.07;
    font-weight: 540;
    letter-spacing: -.035em;
  }

  p {
    margin: 0;
    color: #60737d;
    font-size: 15px;
    line-height: 24px;
  }
`;

export const ExpansionPath = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 18px;

  @media (max-width: 680px) { grid-template-columns: 1fr; }
`;

export const ExpansionStep = styled.div`
  position: relative;
  min-height: 106px;
  padding: 17px;
  border: 1px solid #d6e3e9;
  border-radius: 11px;
  background: #ffffff;

  &:not(:last-child)::after {
    content: "→";
    position: absolute;
    top: 50%;
    right: -17px;
    z-index: 2;
    color: #81a5b5;
    transform: translateY(-50%);
  }

  span,
  strong { display: block; }

  span {
    margin-bottom: 12px;
    color: #1499df;
    font-size: 9px;
    line-height: 15px;
    font-weight: 650;
    letter-spacing: .07em;
  }

  strong {
    color: #2d4c5a;
    font-size: 14px;
    line-height: 21px;
    font-weight: 560;
  }

  @media (max-width: 680px) {
    min-height: 0;
    &:not(:last-child)::after { content: "↓"; top: auto; right: 50%; bottom: -18px; transform: translateX(50%); }
  }
`;

export const ExpansionBridge = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 14px;

  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

export const ExpansionTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 700px) { flex-direction: row; overflow-x: auto; }
`;

export const ExpansionTab = styled.button`
  min-height: 72px;
  padding: 13px 15px;
  border: 1px solid #d4e1e7;
  border-radius: 10px;
  color: #6e828c;
  background: #ffffff;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  line-height: 18px;
  text-align: left;

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    border-color: #173846;
    outline: none;
    color: #ffffff;
    background: #173846;
  }

  @media (max-width: 700px) { flex: 0 0 180px; }
`;

export const ExpansionAudience = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, .8fr) minmax(0, 1.2fr);
  gap: 28px;
  min-height: 220px;
  padding: clamp(24px, 4vw, 40px);
  border: 1px solid #d4e1e7;
  border-radius: 12px;
  background: #ffffff;

  h4 {
    margin: 0;
    color: #173846;
    font-size: clamp(24px, 3vw, 36px);
    line-height: 1.08;
    font-weight: 550;
    letter-spacing: -.025em;
  }

  p {
    margin: 0;
    color: #5d737e;
    font-size: 14px;
    line-height: 23px;
  }

  @media (max-width: 700px) { grid-template-columns: 1fr; min-height: 0; }
`;

export const Layers = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 104px) max(7vw, 48px);
  color: #ffffff;
  background: #101719;
`;

export const LayersInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const LayersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 30px;
  align-items: flex-end;
  margin-bottom: 30px;

  h3 {
    max-width: 640px;
    margin: 0;
    font-size: clamp(31px, 4vw, 52px);
    line-height: 1.07;
    font-weight: 530;
    letter-spacing: -.03em;
  }

  p {
    margin: 0 0 5px;
    color: #9fb1ba;
    font-size: 13px;
    line-height: 20px;
    font-style: italic;
  }

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const LayerExplorerLayout = styled.div`
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const LayerTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;

  @media (max-width: 720px) {
    flex-direction: row;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
  }
`;

export const LayerTab = styled.button`
  display: flex;
  gap: 11px;
  align-items: center;
  min-height: 55px;
  padding: 12px 14px;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 9px;
  color: #83959d;
  background: rgba(255,255,255,.03);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  line-height: 19px;
  text-align: left;

  span {
    color: #60757e;
    font-size: 10px;
    font-weight: 650;
  }

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    border-color: rgba(62,190,255,.65);
    outline: none;
    color: #ffffff;
    background: rgba(21,151,229,.16);

    span { color: ${blue}; }
  }

  @media (max-width: 720px) {
    flex: 0 0 150px;
  }
`;

export const LayerBody = styled.div`
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: clamp(26px, 5vw, 66px);
  align-items: center;
  min-height: 304px;
  padding: clamp(30px, 5vw, 58px);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(14,66,88,.78), rgba(18,29,34,.94));

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`;

export const LayerIndex = styled.div`
  color: ${blue};
  font-size: clamp(82px, 11vw, 142px);
  line-height: .8;
  font-weight: 520;
  letter-spacing: -.07em;
`;

export const LayerCopy = styled.div`
  span {
    display: block;
    margin-bottom: 10px;
    color: ${lime};
    font-size: 11px;
    line-height: 17px;
    font-weight: 640;
    letter-spacing: .08em;
  }

  h4 {
    margin: 0 0 14px;
    font-size: clamp(26px, 3.6vw, 44px);
    line-height: 1.08;
    font-weight: 530;
    letter-spacing: -.025em;
  }

  p {
    max-width: 650px;
    margin: 0;
    color: #bccbd1;
    font-size: 15px;
    line-height: 25px;
  }
`;

export const PicSystem = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 100px) max(7vw, 48px);
  background: #f3f7f9;
`;

export const PicInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const PicHeader = styled.div`
  max-width: 760px;
  margin-bottom: 32px;

  h3 {
    margin: 0 0 12px;
    color: ${ink};
    font-size: clamp(31px, 4vw, 50px);
    line-height: 1.08;
    font-weight: 530;
    letter-spacing: -.03em;
  }

  p {
    margin: 0;
    color: #60737d;
    font-size: 15px;
    line-height: 24px;
  }
`;

export const ProcessMap = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const ProcessNode = styled.button<{ area: string }>`
  position: relative;
  min-height: 150px;
  padding: 54px 16px 17px;
  border: 1px solid #d8e3e8;
  border-radius: 12px;
  color: #35505e;
  background: #ffffff;
  cursor: pointer;
  font: inherit;
  text-align: left;

  &::after {
    content: "→";
    position: absolute;
    z-index: 2;
    top: 50%;
    right: -18px;
    width: 18px;
    color: #8fb4c5;
    font-size: 16px;
    text-align: center;
    transform: translateY(-50%);
  }

  &:last-child::after { display: none; }

  span,
  strong { display: block; }

  span {
    position: absolute;
    top: 16px;
    left: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: #1499df;
    background: #e8f6fd;
    font-size: 10px;
    line-height: 16px;
    font-weight: 640;
  }

  strong {
    margin-bottom: 7px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 570;
  }

  small {
    display: block;
    color: #81919a;
    font-size: 11px;
    line-height: 17px;
  }

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    border-color: #1499df;
    outline: none;
    color: #ffffff;
    background: #173846;

    span { color: #ffffff; background: #1499df; }
    small { color: #b9ccd4; }
  }

  @media (max-width: 900px) {
    min-height: 0;
    padding: 18px 18px 18px 62px;

    &::after {
      content: "↓";
      top: auto;
      right: 50%;
      bottom: -18px;
      transform: translateX(50%);
    }
  }
`;

export const ProcessDetail = styled.div`
  display: grid;
  grid-template-columns: minmax(190px, .55fr) minmax(0, 1.45fr);
  gap: 28px;
  margin: 22px 0 0;
  padding: 26px;
  border: 1px solid #d8e3e8;
  border-radius: 12px;
  background: #ffffff;

  h4 {
    margin: 0;
    color: ${ink};
    font-size: 19px;
    line-height: 26px;
    font-weight: 550;
  }

  p {
    margin: 0;
    color: #526872;
    font-size: 14px;
    line-height: 23px;
  }

  dl {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin: 0;
  }

  dl div {
    padding: 14px;
    border-radius: 8px;
    background: #f4f8fa;
  }

  dt,
  dd { margin: 0; }

  dt {
    margin-bottom: 4px;
    color: #8a9aa2;
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
    letter-spacing: .07em;
  }

  dd {
    color: #2e4b59;
    font-size: 12px;
    line-height: 19px;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;

    dl { grid-column: 1; grid-template-columns: 1fr; }
  }
`;

export const BriefBoard = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 100px) max(7vw, 48px);
  background: #ffffff;
`;

export const BriefInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const BriefHeader = styled(PicHeader)``;

export const BriefGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

export const BriefColumn = styled.div<{ tone: "green" | "blue" | "purple" }>`
  overflow: hidden;
  border: 1px solid #dbe4e8;
  border-radius: 12px;
  background: #fbfcfd;

  h4 {
    margin: 0;
    padding: 13px 16px;
    color: #ffffff;
    background: ${(props) =>
      props.tone === "green" ? "#54bd8d" : props.tone === "blue" ? "#4c91e7" : "#8a47e8"};
    font-size: 13px;
    line-height: 19px;
    font-weight: 570;
  }
`;

export const BriefItems = styled.div`
  display: grid;
  gap: 9px;
  padding: 15px;
`;

export const BriefItem = styled.div`
  min-height: 66px;
  padding: 13px 14px;
  border: 1px solid #e1e7ea;
  border-radius: 8px;
  color: #49606c;
  background: #ffffff;
  font-size: 12px;
  line-height: 18px;

  strong {
    display: block;
    margin-bottom: 4px;
    color: #243f4c;
    font-size: 12px;
    font-weight: 570;
  }
`;

export const BriefFootnote = styled.p`
  margin: 14px 0 0;
  color: #83939b;
  font-size: 11px;
  line-height: 18px;
`;

export const ResearchShell = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 7vw, 92px) max(7vw, 48px);
  color: #ffffff;
  background: #102f3d;
`;

export const ResearchTabs = styled.div`
  display: flex;
  gap: 8px;
  max-width: 1080px;
  margin: 0 auto 16px;
`;

export const ResearchTab = styled.button`
  padding: 11px 15px;
  border: 1px solid rgba(255,255,255,.16);
  border-radius: 999px;
  color: #9fb5be;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    outline: none;
    color: #102f3d;
    background: #ffffff;
  }
`;

export const ResearchPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(270px, .72fr) minmax(0, 1.28fr);
  gap: clamp(30px, 5vw, 70px);
  align-items: center;
  max-width: 1080px;
  margin: 0 auto;
  padding: clamp(28px, 5vw, 52px);
  border: 1px solid rgba(255,255,255,.13);
  border-radius: 16px;
  background: rgba(6,25,33,.34);

  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

export const ResearchText = styled.div`
  > span {
    display: block;
    margin-bottom: 11px;
    color: ${lime};
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
    letter-spacing: .08em;
  }

  h3 {
    margin: 0 0 14px;
    font-size: clamp(29px, 3.8vw, 45px);
    line-height: 1.08;
    font-weight: 530;
    letter-spacing: -.03em;
  }

  p {
    margin: 0;
    color: #b8cbd3;
    font-size: 15px;
    line-height: 25px;
  }
`;

export const ComparisonRoute = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 26px;

  i {
    position: relative;
    padding: 9px 11px;
    border: 1px solid rgba(255,255,255,.17);
    border-radius: 7px;
    color: #d3e4eb;
    background: rgba(255,255,255,.05);
    font-size: 11px;
    line-height: 16px;
    font-style: normal;
  }

  i:not(:last-child)::after {
    content: "→";
    position: absolute;
    right: -17px;
    color: #6ca3bb;
  }
`;

export const NeedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 26px;

  i {
    padding: 10px 12px;
    border-left: 2px solid ${blue};
    color: #d3e4eb;
    background: rgba(255,255,255,.05);
    font-size: 11px;
    line-height: 17px;
    font-style: normal;
  }
`;

export const ResearchImageButton = styled.button`
  position: relative;
  display: block;
  width: 100%;
  min-height: 400px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,.15);
  border-radius: 12px;
  background: #172428;
  cursor: zoom-in;

  img {
    display: block;
    width: 100%;
    height: clamp(310px, 38vw, 520px);
    object-fit: contain;
  }

  span {
    position: absolute;
    right: 12px;
    bottom: 12px;
    padding: 7px 10px;
    border-radius: 999px;
    color: #ffffff;
    background: rgba(7,22,29,.86);
    font-size: 11px;
  }
`;

export const Lightbox = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 24px 24px;
  background: rgba(7,13,16,.95);

  img {
    display: block;
    max-width: min(1500px, 96vw);
    max-height: calc(100vh - 92px);
    object-fit: contain;
  }
`;

export const LightboxClose = styled.button`
  position: absolute;
  top: 18px;
  right: 22px;
  padding: 8px 12px;
  border: 1px solid rgba(255,255,255,.35);
  border-radius: 999px;
  color: #ffffff;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
`;

export const OutcomeBoard = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 100px) max(7vw, 48px);
  background: #f5f8fa;
`;

export const OutcomeHeader = styled(PicHeader)`
  max-width: 720px;
`;

export const OutcomeTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  max-width: 1080px;
  margin: 0 auto 14px;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

export const OutcomeTab = styled.button`
  position: relative;
  min-height: 116px;
  padding: 18px 18px 18px 56px;
  border: 1px solid #d7e2e7;
  border-radius: 12px;
  color: #35505e;
  background: #ffffff;
  cursor: pointer;
  font: inherit;
  text-align: left;

  span {
    position: absolute;
    top: 19px;
    left: 18px;
    color: ${blue};
    font-size: 11px;
    font-weight: 650;
  }

  strong,
  small { display: block; }

  strong {
    margin-bottom: 5px;
    font-size: 15px;
    line-height: 21px;
    font-weight: 580;
  }

  small {
    color: #7e929d;
    font-size: 11px;
    line-height: 17px;
  }

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    border-color: #173846;
    outline: none;
    color: #ffffff;
    background: #173846;

    small { color: #b9ccd4; }
  }
`;

export const OutcomePanel = styled.div`
  display: grid;
  grid-template-columns: minmax(250px, .7fr) minmax(0, 1.3fr);
  gap: clamp(28px, 5vw, 70px);
  max-width: 1080px;
  min-height: 310px;
  margin: 0 auto;
  padding: clamp(28px, 5vw, 50px);
  border: 1px solid #d7e2e7;
  border-radius: 14px;
  background: #ffffff;

  @media (max-width: 790px) { grid-template-columns: 1fr; }
`;

export const OutcomeIntro = styled.div`
  > span {
    display: block;
    margin-bottom: 10px;
    color: ${blue};
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
    letter-spacing: .07em;
  }

  h4 {
    margin: 0 0 13px;
    color: ${ink};
    font-size: clamp(24px, 3vw, 35px);
    line-height: 1.1;
    font-weight: 540;
    letter-spacing: -.025em;
  }

  p {
    margin: 0;
    color: #617580;
    font-size: 14px;
    line-height: 23px;
  }
`;

export const StoryFlow = styled.div`
  display: grid;
  gap: 12px;
  align-content: center;
`;

export const StoryStep = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 16px;
  align-items: center;
  padding: 17px;
  border-radius: 10px;
  background: #f1f7fa;

  &:not(:last-child)::after {
    content: "↓";
    position: absolute;
    bottom: -16px;
    left: 43px;
    z-index: 2;
    color: ${blue};
  }

  span {
    color: ${blue};
    font-size: 22px;
    line-height: 28px;
    font-weight: 590;
  }

  strong {
    color: #37515e;
    font-size: 13px;
    line-height: 20px;
    font-weight: 560;
  }
`;

export const WorkshopVisual = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  align-content: center;

  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

export const WorkshopCell = styled.div<{ result?: boolean }>`
  grid-column: ${(props) => (props.result ? "1 / -1" : "auto")};
  padding: 18px;
  border: 1px solid ${(props) => (props.result ? "#1499df" : "#d9e4e9")};
  border-radius: 10px;
  background: ${(props) => (props.result ? "#eaf7fd" : "#f8fafb")};
  text-align: center;

  span,
  strong { display: block; }

  span {
    margin-bottom: 5px;
    color: #84959d;
    font-size: 9px;
    line-height: 15px;
    font-weight: 650;
    letter-spacing: .07em;
  }

  strong {
    color: #35505d;
    font-size: 12px;
    line-height: 18px;
    font-weight: 560;
  }
`;

export const PrioritySheet = styled.div`
  overflow-x: auto;
  border: 1px solid #d7e1e6;
  border-radius: 9px;
  background: #ffffff;

  table {
    width: 100%;
    min-width: 650px;
    border-collapse: collapse;
    color: #48616d;
    font-size: 11px;
    line-height: 17px;
  }

  th,
  td {
    padding: 11px 12px;
    border-right: 1px solid #e3e9ec;
    border-bottom: 1px solid #e3e9ec;
    text-align: left;
    vertical-align: middle;
  }

  th {
    color: #ffffff;
    background: #347fa4;
    font-size: 10px;
    font-weight: 620;
  }

  tr:last-child td { border-bottom: 0; }
  th:last-child, td:last-child { border-right: 0; }

  td:first-child { width: 35%; color: #2f4d5b; font-weight: 550; }

  td span {
    display: inline-block;
    padding: 4px 7px;
    border-radius: 999px;
    color: #1478aa;
    background: #e5f5fc;
    white-space: nowrap;
  }
`;

export const Impact = styled.section`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(230px, .7fr) minmax(0, 1.3fr);
  gap: clamp(30px, 6vw, 90px);
  width: 100%;
  padding: clamp(62px, 8vw, 108px) max(7vw, 48px);
  color: #ffffff;
  background: #1495d8;

  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

export const ImpactLabel = styled.div`
  span {
    display: block;
    margin-bottom: 13px;
    color: rgba(255,255,255,.75);
    font-size: 11px;
    line-height: 17px;
    font-weight: 650;
    letter-spacing: .08em;
  }

  strong {
    display: block;
    font-size: clamp(48px, 7vw, 90px);
    line-height: .9;
    font-weight: 550;
    letter-spacing: -.055em;
  }
`;

export const ImpactCopy = styled.div`
  h3 {
    max-width: 720px;
    margin: 0 0 18px;
    font-size: clamp(29px, 4vw, 48px);
    line-height: 1.1;
    font-weight: 530;
    letter-spacing: -.03em;
  }

  p {
    max-width: 680px;
    margin: 0;
    color: rgba(255,255,255,.84);
    font-size: 15px;
    line-height: 24px;
  }
`;
